// src/components/BottomNavBar.tsx
//
// BekalNanti — Milestone P3.6: Mobile Bottom Navigation Bar
// Provides persistent top-level destinations (Beranda | Orang | Rencana | Keuangan).

import React from 'react'
import { Compass, Users, Target, DollarSign } from 'lucide-react'

export type NavTabId = 'beranda' | 'orang' | 'rencana' | 'keuangan'

interface BottomNavBarProps {
  activeTab: NavTabId
  onSelectTab: (tab: NavTabId) => void
  peopleCount?: number
  planCount?: number
}

export const BottomNavBar: React.FC<BottomNavBarProps> = ({
  activeTab,
  onSelectTab,
  peopleCount,
  planCount,
}) => {
  const tabs: Array<{
    id: NavTabId
    label: string
    icon: React.ComponentType<{ size: number; className?: string }>
    badge?: number
  }> = [
    { id: 'beranda', label: 'Beranda', icon: Compass },
    { id: 'orang', label: 'Orang', icon: Users, badge: peopleCount },
    { id: 'rencana', label: 'Rencana', icon: Target, badge: planCount },
    { id: 'keuangan', label: 'Keuangan', icon: DollarSign },
  ]

  return (
    <nav
      aria-label="Navigasi Utama"
      className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/80 shadow-[0_-4px_20px_rgba(0,0,0,0.04)] sm:hidden"
    >
      <div className="max-w-md mx-auto grid grid-cols-4 h-16 px-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id
          const Icon = tab.icon

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSelectTab(tab.id)}
              className={`flex flex-col items-center justify-center gap-1 transition-all cursor-pointer relative py-1 ${
                isActive
                  ? 'text-[#0F766E]'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <div className="relative">
                <Icon
                  size={20}
                  className={`transition-transform ${
                    isActive ? 'scale-110 text-[#0EA5A4]' : 'text-slate-400'
                  }`}
                />
                {Boolean(tab.badge && tab.badge > 0) && (
                  <span className="absolute -top-1 -right-2 px-1 min-w-[14px] h-3.5 rounded-full bg-teal-100 text-[#0F766E] text-[9px] font-bold flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </div>
              <span
                className={`text-[11px] leading-tight ${
                  isActive ? 'font-bold text-[#0F766E]' : 'font-medium text-slate-500'
                }`}
              >
                {tab.label}
              </span>
              {isActive && (
                <span className="absolute bottom-1 w-6 h-0.5 rounded-full bg-[#0EA5A4]" />
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
